import React, { useState, useEffect } from 'react';
import { 
  FaEnvelope, FaUser, FaPhone, FaCalendarAlt,
  FaCheckCircle, FaTimesCircle, FaTrash,
  FaEye, FaEyeSlash, FaFilter, FaSearch,
  FaExclamationTriangle, FaSync, FaSpinner,
  FaTimes, FaReply
} from 'react-icons/fa';
import { contactAPI, utilsAPI } from '../../../api';
import './ContactListe.css';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const ContactListe = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRead, setFilterRead] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Charger les contacts
  const loadContacts = async () => {
    try {
      setLoading(true);
      setError('');

      // Vérifier l'authentification
      if (!utilsAPI.isAuthenticated()) {
        setError('Veuillez vous connecter pour accéder aux messages');
        setLoading(false);
        return;
      }

      // Récupérer les contacts
      const response = await contactAPI.getAll();
      
      if (response.success) {
        // Extraire les données selon la structure
        let contactsData = [];
        if (response.data && response.data.data) {
          contactsData = response.data.data;
        } else if (Array.isArray(response.data)) {
          contactsData = response.data;
        }
        
        setContacts(contactsData);
        
        // Calculer le nombre de messages non lus
        const unread = contactsData.filter(contact => !contact.is_read).length;
        setUnreadCount(unread);
      } else {
        setError(response.message || 'Erreur lors du chargement des messages');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError('Impossible de se connecter au serveur');
    } finally {
      setLoading(false);
    }
  };

  // Marquer comme lu/non lu
  const toggleReadStatus = async (contactId, isRead) => {
    try {
      setIsUpdating(true);
      let response;
      
      if (isRead) {
        // Marquer comme non lu - utiliser POST sur mark-unread
        response = await contactAPI.markAsUnread(contactId);
      } else {
        // Marquer comme lu - utiliser POST sur mark-read
        response = await contactAPI.markAsRead(contactId);
      }

      if (response.success) {
        // Mettre à jour l'état local
        setContacts(prev => prev.map(contact => 
          contact.id === contactId 
            ? { ...contact, is_read: !isRead } 
            : contact
        ));
        
        // Mettre à jour le compteur
        setUnreadCount(prev => isRead ? prev + 1 : prev - 1);

        // Si le contact affiché dans la modale est celui qu'on modifie
        if (selectedContact && selectedContact.id === contactId) {
          setSelectedContact({ ...selectedContact, is_read: !isRead });
        }
      } else {
        alert(response.message || 'Erreur lors du changement de statut');
      }
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur: ' + (err.message || 'Erreur inconnue'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Supprimer un message
  const handleDelete = async (contactId, contactName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le message de "${contactName}" ?`)) {
      return;
    }

    try {
      setIsDeleting(contactId);
      
      const response = await contactAPI.delete(contactId);
      
      if (response.success) {
        // Supprimer de l'état local
        setContacts(prev => {
          const updatedContacts = prev.filter(contact => contact.id !== contactId);
          
          // Recalculer le nombre de non lus
          const unread = updatedContacts.filter(contact => !contact.is_read).length;
          setUnreadCount(unread);
          
          return updatedContacts;
        });
        
        // Fermer la modale si le contact supprimé était affiché
        if (selectedContact && selectedContact.id === contactId) {
          setShowModal(false);
          setSelectedContact(null);
        }
        
        alert('Message supprimé avec succès');
      } else {
        alert(response.message || 'Erreur lors de la suppression');
      }
    } catch (err) {
      console.error('Erreur:', err);
      alert('Erreur: ' + (err.message || 'Erreur inconnue'));
    } finally {
      setIsDeleting(null);
    }
  };

  // Ouvrir la modale avec les détails du message
  const openMessageDetails = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
    
    // Si le message n'est pas lu, le marquer automatiquement comme lu
    if (!contact.is_read) {
      toggleReadStatus(contact.id, contact.is_read);
    }
  };

  // Fermer la modale
  const closeModal = () => {
    setShowModal(false);
    setSelectedContact(null);
  };

  useEffect(() => {
    loadContacts();
  }, []);

  // Filtrer les contacts
  const filteredContacts = contacts.filter(contact => {
    // Filtre par statut
    if (filterRead === 'read' && !contact.is_read) return false;
    if (filterRead === 'unread' && contact.is_read) return false;
    
    // Recherche
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (
        (contact.name?.toLowerCase().includes(term)) ||
        (contact.email?.toLowerCase().includes(term)) ||
        (contact.subject?.toLowerCase().includes(term)) ||
        (contact.message?.toLowerCase().includes(term)) ||
        (contact.phone?.toLowerCase().includes(term))
      );
    }
    
    return true;
  });

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Tronquer le texte
  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (loading) {
    return (
      <div className="contact-liste-container">
        <div className="loading-containerjdj">
          <FaSpinner className="loading-spinnerdjj" />
          <p>Chargement des messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="contact-liste-container">
      <Link 
        to="/admin" 
        className="btn-back-admin"
        title="Retour à l'administration"
      >   
        <FaArrowLeft className="back-arrow-icon" />
      </Link>
      
      {/* En-tête */}
      <div className="contact-header">
        <h2>Messages de contact</h2>
        <div className="stats">
          <span className="total">
            <FaEnvelope /> {contacts.length} message{contacts.length !== 1 ? 's' : ''}
          </span>
          {unreadCount > 0 && (
            <span className="unread">{unreadCount} non lu{unreadCount !== 1 ? 's' : ''}</span>
          )}
        </div>
      </div>

      {/* Barre de recherche/filtre */}
      <div className="toolbar">
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-box">
          <FaFilter />
          <select value={filterRead} onChange={(e) => setFilterRead(e.target.value)}>
            <option value="all">Tous les messages</option>
            <option value="unread">Non lus</option>
            <option value="read">Lus</option>
          </select>
        </div>

        <button onClick={loadContacts} className="refresh-btn" disabled={loading}>
          <FaSync /> {loading ? 'Chargement...' : 'Actualiser'}
        </button>
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="error-message">
          <FaExclamationTriangle /> {error}
          <button onClick={loadContacts} className="retry-btn">
            Réessayer
          </button>
        </div>
      )}

      {/* Liste des contacts */}
      <div className="contacts-list">
        {filteredContacts.length === 0 ? (
          <div className="empty-state">
            <FaEnvelope />
            <h3>Aucun message</h3>
            <p>
              {searchTerm || filterRead !== 'all'
                ? 'Aucun résultat pour vos critères de recherche'
                : 'Aucun message de contact pour le moment'
              }
            </p>
            {(searchTerm || filterRead !== 'all') && (
              <button 
                onClick={() => {
                  setSearchTerm('');
                  setFilterRead('all');
                }}
                className="clear-filters-btn"
              >
                Effacer les filtres
              </button>
            )}
          </div>
        ) : (
          filteredContacts.map(contact => (
            <div key={contact.id} className={`contact-card ${contact.is_read ? 'read' : 'unread'}`}>
              <div className="card-header">
                <div className="avatar">
                  {contact.name?.charAt(0) || '?'}
                </div>
                <div className="info">
                  <h3>{contact.name || 'Anonyme'}</h3>
                  <p className="email">{contact.email}</p>
                  {contact.phone && <p className="phone">{contact.phone}</p>}
                </div>
                <div className="status">
                  {contact.is_read ? (
                    <span className="badge read"><FaCheckCircle /> Lu</span>
                  ) : (
                    <span className="badge unread"><FaTimesCircle /> Non lu</span>
                  )}
                </div>
              </div>
              
              <div className="card-body">
                <h4>{contact.subject}</h4>
                <p className="message-preview">
                  {truncateText(contact.message, 150)}
                </p>
                <div className="meta">
                  <span className="date">
                    <FaCalendarAlt /> {formatDate(contact.created_at)}
                  </span>
                </div>
              </div>
              
              <div className="card-actions">
                <button 
                  onClick={() => openMessageDetails(contact)}
                  className="action-btn view-btn"
                  title="Voir le message en détail"
                >
                  <FaEye /> Voir détail
                </button>
                <button 
                  onClick={() => toggleReadStatus(contact.id, contact.is_read)}
                  className={`action-btn status-btn ${contact.is_read ? 'unread' : 'read'}`}
                  disabled={isUpdating || isDeleting === contact.id}
                  title={contact.is_read ? 'Marquer comme non lu' : 'Marquer comme lu'}
                >
                  {isUpdating ? <FaSpinner className="spinning" /> : (contact.is_read ? <FaEyeSlash /> : <FaEye />)}
                  {isUpdating ? 'Mise à jour...' : (contact.is_read ? 'Marquer non lu' : 'Marquer lu')}
                </button>
                <button 
                  onClick={() => handleDelete(contact.id, contact.name)}
                  className="action-btn delete-btn"
                  disabled={isUpdating || isDeleting === contact.id}
                  title="Supprimer ce message"
                >
                  {isDeleting === contact.id ? <FaSpinner className="spinning" /> : <FaTrash />}
                  {isDeleting === contact.id ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal pour afficher les détails du message */}
      {showModal && selectedContact && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Détail du message</h2>
              <button className="modal-close" onClick={closeModal}>
                <FaTimes />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-info-section">
                <div className="info-row">
                  <FaUser className="info-icon" />
                  <div className="info-content">
                    <label>Nom complet</label>
                    <p>{selectedContact.name || 'Non renseigné'}</p>
                  </div>
                </div>
                
                <div className="info-row">
                  <FaEnvelope className="info-icon" />
                  <div className="info-content">
                    <label>Adresse email</label>
                    <p>{selectedContact.email || 'Non renseigné'}</p>
                  </div>
                </div>
                
                {selectedContact.phone && (
                  <div className="info-row">
                    <FaPhone className="info-icon" />
                    <div className="info-content">
                      <label>Téléphone</label>
                      <p>{selectedContact.phone}</p>
                    </div>
                  </div>
                )}
                
                <div className="info-row">
                  <FaCalendarAlt className="info-icon" />
                  <div className="info-content">
                    <label>Date d'envoi</label>
                    <p>{formatDate(selectedContact.created_at)}</p>
                  </div>
                </div>
                
                <div className="info-row">
                  <div className="info-content full-width">
                    <label>Sujet</label>
                    <p className="modal-subject">{selectedContact.subject}</p>
                  </div>
                </div>
                
                <div className="info-row">
                  <div className="info-content full-width">
                    <label>Message complet</label>
                    <div className="modal-message">
                      {selectedContact.message || 'Aucun message'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={() => {
                  closeModal();
                  toggleReadStatus(selectedContact.id, selectedContact.is_read);
                }}
                className={`modal-status-btn ${selectedContact.is_read ? 'unread' : 'read'}`}
              >
                {selectedContact.is_read ? <FaEyeSlash /> : <FaEye />}
                {selectedContact.is_read ? 'Marquer comme non lu' : 'Marquer comme lu'}
              </button>
              <button 
                onClick={() => {
                  handleDelete(selectedContact.id, selectedContact.name);
                }}
                className="modal-delete-btn"
                disabled={isDeleting === selectedContact.id}
              >
                {isDeleting === selectedContact.id ? <FaSpinner className="spinning" /> : <FaTrash />}
                {isDeleting === selectedContact.id ? 'Suppression...' : 'Supprimer'}
              </button>
              <button onClick={closeModal} className="modal-close-btn">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statistiques */}
      {filteredContacts.length > 0 && (
        <div className="footer-stats">
          <p>
            Affichage de {filteredContacts.length} message{filteredContacts.length !== 1 ? 's' : ''} 
            {filteredContacts.length !== contacts.length && ` (sur ${contacts.length} au total)`}
          </p>
        </div>
      )}
    </div>
  );
};

export default ContactListe;